import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Stack,
  TextField,
  IconButton,
  Button,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import {
  MediaUploadService,
  UploadImageCategory,
} from '../services/MediaUploadService';

interface MultiImageManagerProps {
  imageUrls: string[];
  onImageUrlsChange: (newImageUrls: string[]) => void;
  isReadOnly: boolean;
  helperText?: string;
  /** Where uploaded files are stored on S3. Defaults to the cause gallery prefix. */
  uploadCategory?: UploadImageCategory;
}

const MultiImageManager: React.FC<MultiImageManagerProps> = ({
  imageUrls,
  onImageUrlsChange,
  isReadOnly,
  helperText,
  uploadCategory = 'cause-gallery',
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleImageChange = (index: number, value: string) => {
    const newImageUrls = [...imageUrls];
    newImageUrls[index] = value;
    onImageUrlsChange(newImageUrls);
  };

  const handleAddImage = () => {
    onImageUrlsChange([...imageUrls, '']);
  };

  const handleRemoveImage = (index: number) => {
    const newImageUrls = [...imageUrls];
    newImageUrls.splice(index, 1);
    onImageUrlsChange(newImageUrls);
  };

  const handleUploadImages = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const uploaded = await Promise.all(
        files.map((file) =>
          MediaUploadService.uploadImageFile(file, uploadCategory),
        ),
      );
      onImageUrlsChange([...imageUrls, ...uploaded.map((result) => result.url)]);
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Upload failed.';
      setUploadError(`Image upload failed: ${serverMessage}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Cover Images
      </Typography>
      {helperText ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helperText}
        </Typography>
      ) : null}
      {uploadError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      ) : null}
      <Stack spacing={2}>
        {imageUrls.map((url, index) => (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            key={index}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Box
              sx={{
                width: { xs: '100%', md: 96 },
                height: 72,
                borderRadius: 2,
                backgroundColor: 'action.hover',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundImage: url ? `url(${url})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <TextField
              label={`Image URL ${index + 1}`}
              value={url}
              onChange={(e) => handleImageChange(index, e.target.value)}
              fullWidth
              disabled={isReadOnly}
              helperText={index === 0 ? 'The first image is used as the default cover.' : ' '}
              InputProps={{ readOnly: isReadOnly }}
            />
            {!isReadOnly && (
              <IconButton
                onClick={() => handleRemoveImage(index)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        ))}
        {!isReadOnly && (
          <Stack direction="row" spacing={1.5}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddImage}
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
              disabled={uploading}
            >
              Add URL
            </Button>
            <Button
              variant="contained"
              onClick={() => inputRef.current?.click()}
              sx={{ alignSelf: 'flex-start' }}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Upload From Gallery'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleUploadImages}
            />
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default MultiImageManager;
